import type { FractalPoints } from "../../model/calcFractal";
import type { FractalSettings } from "../../model";
import type { ZoomRange, CandleWithFlag } from "features/chart";
import { GRID, CANDLE_MIN_WIDTH } from "features/chart";

const ARROW_W = 6;
const ARROW_H = 10;
const ARROW_OFFSET = 18;

function drawTriangle(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  pointingUp: boolean,
): void {
  ctx.beginPath();
  if (pointingUp) {
    ctx.moveTo(x, y);
    ctx.lineTo(x - ARROW_W / 2, y + ARROW_H);
    ctx.lineTo(x + ARROW_W / 2, y + ARROW_H);
  } else {
    ctx.moveTo(x, y);
    ctx.lineTo(x - ARROW_W / 2, y - ARROW_H);
    ctx.lineTo(x + ARROW_W / 2, y - ARROW_H);
  }
  ctx.closePath();
  ctx.fill();
}

export function drawFractal(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  data: CandleWithFlag[],
  zoom: ZoomRange,
  points: FractalPoints,
  settings: FractalSettings,
): void {
  const { left, right, top, bottom } = GRID;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const { start, end } = zoom;
  const startIdx = Math.floor(start);
  const endIdx = Math.min(Math.floor(end), data.length - 1);
  const visibleCount = Math.floor(end) - startIdx + 1;
  const cw = Math.max(CANDLE_MIN_WIDTH, chartWidth / visibleCount);

  let minPrice = Infinity,
    maxPrice = -Infinity;
  for (let i = startIdx; i <= endIdx; i++) {
    const c = data[i];
    if (!c) continue;
    minPrice = Math.min(minPrice, c.low);
    maxPrice = Math.max(maxPrice, c.high);
  }
  if (minPrice === Infinity) return;
  const priceRange = maxPrice - minPrice || 1;
  const pad = priceRange * 0.05;
  minPrice -= pad;
  maxPrice += pad;
  const range = maxPrice - minPrice;
  const priceToY = (p: number) => top + ((maxPrice - p) / range) * chartHeight;

  ctx.clearRect(0, 0, width, height);

  for (let i = startIdx; i <= endIdx; i++) {
    const x = left + (i - startIdx + 0.5) * cw;

    if (settings.up.visible && points.up[i]) {
      ctx.fillStyle = settings.up.color;
      drawTriangle(ctx, x, priceToY(data[i].high) - ARROW_OFFSET, true);
    }

    if (settings.down.visible && points.down[i]) {
      ctx.fillStyle = settings.down.color;
      drawTriangle(ctx, x, priceToY(data[i].low) + ARROW_OFFSET, false);
    }
  }
}
